/**
 * كتالوج المقابلات والتقييم.
 *
 * ما بين «وصل طلبُه» و«عيّنّاه» ثلاثةُ أشياء يخلطها كثيرٌ من الأنظمة في
 * حقلٍ واحد: موعدٌ يُحجز، ولقاءٌ يقع أو لا يقع، وحكمٌ يُكتب بعده. فصلُها
 * يجعل السؤال «كم مقابلةً لم تقع» قابلًا للجواب — وهو أكثرُ ما يُهدر
 * وقتَ الفريق.
 *
 * والتقييمُ رأيُ شخصٍ لا رقمُ آلة: التوصيةُ هي القرار، والدرجاتُ ترتّب
 * المتقاربين. ونظامٌ يقلب متوسّطًا حسابيًّا إلى قبولٍ يجعل المقيّم يكتب
 * الأرقام التي تُنتج ما يريد بدل أن يكتب ما رأى.
 */
import type { Labelled } from './common.js';

/* ==========================================================================
   نوع اللقاء
   ========================================================================== */

export const INTERVIEW_KINDS = ['PHONE', 'ONLINE', 'ONSITE', 'TEST'] as const;
export type InterviewKind = (typeof INTERVIEW_KINDS)[number];

export interface InterviewKindDef extends Labelled<InterviewKind> {
  description: string;
  /** يحتاج رابطَ اجتماع. */
  needsLink: boolean;
  /** يحتاج مكانًا يُذكر للمرشّح. */
  needsPlace: boolean;
  /** مدّةٌ افتراضيةٌ بالدقائق — تُقترح ولا تُفرض. */
  minutes: number;
}

export const INTERVIEW_KIND_DEFS: readonly InterviewKindDef[] = [
  {
    key: 'PHONE',
    label: 'مكالمة',
    kind: 'info',
    description: 'اتّصالٌ قصير: الجدّية والراتب والإتاحة قبل إنفاق وقت أحد.',
    needsLink: false,
    needsPlace: false,
    minutes: 15,
  },
  {
    key: 'ONLINE',
    label: 'اجتماع أونلاين',
    kind: 'brand',
    description: 'لقاءٌ مرئيٌّ برابطٍ يُرسل إليه.',
    needsLink: true,
    needsPlace: false,
    minutes: 45,
  },
  {
    key: 'ONSITE',
    label: 'مقابلة حضورية',
    kind: 'success',
    description: 'يحضر إلى المقرّ — والعنوان يُكتب له لا يُفترض أنّه يعرفه.',
    needsLink: false,
    needsPlace: true,
    minutes: 60,
  },
  {
    key: 'TEST',
    label: 'اختبار عملي',
    kind: 'warning',
    description: 'مهمّةٌ يؤدّيها — حضورًا أو في بيته بمهلة.',
    needsLink: false,
    needsPlace: false,
    minutes: 90,
  },
] as const;

export const INTERVIEW_KIND_MAP = Object.fromEntries(
  INTERVIEW_KIND_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<InterviewKind, InterviewKindDef>>;

/* ==========================================================================
   حالة اللقاء
   ========================================================================== */

/**
 * وقع أم لم يقع.
 *
 * «لم يحضر» حالةٌ مستقلّةٌ عن «أُلغي» عمدًا: الأولى تخصّ المرشّح وتُقرأ في
 * قراره، والثانية تخصّنا. وخلطُهما يجعل تخلُّفَ المرشّحين غيرَ مرئيّ.
 */
export const INTERVIEW_STATUSES = [
  'SCHEDULED',
  'DONE',
  'NO_SHOW',
  'CANCELLED',
] as const;
export type InterviewStatus = (typeof INTERVIEW_STATUSES)[number];

export interface InterviewStatusDef extends Labelled<InterviewStatus> {
  /** ما زال موعدًا قادمًا يُذكّر به. */
  pending: boolean;
  /** وقع فعلًا فيُقبل عليه تقييم. */
  happened: boolean;
}

export const INTERVIEW_STATUS_DEFS: readonly InterviewStatusDef[] = [
  {
    key: 'SCHEDULED',
    label: 'محجوز',
    kind: 'info',
    pending: true,
    happened: false,
  },
  { key: 'DONE', label: 'تمّ', kind: 'success', pending: false, happened: true },
  {
    key: 'NO_SHOW',
    label: 'لم يحضر',
    kind: 'danger',
    pending: false,
    happened: false,
  },
  {
    key: 'CANCELLED',
    label: 'أُلغي',
    kind: 'neutral',
    pending: false,
    happened: false,
  },
] as const;

export const INTERVIEW_STATUS_MAP = Object.fromEntries(
  INTERVIEW_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<InterviewStatus, InterviewStatusDef>>;

/* ==========================================================================
   معايير التقييم
   ========================================================================== */

/**
 * خمسةُ معايير لا خمسة عشر.
 *
 * استمارةُ تقييمٍ طويلةٌ تُملأ بالوسط في كلّ خانةٍ حتّى تنتهي، فتخرج بلا
 * معلومة. والمعاييرُ ثابتةٌ في الحزمة لا تُضبط لكلّ شركة: تقييمٌ يُقارن
 * بين مرشّحين يحتاج المسطرةَ نفسَها، وضبطُها لكلّ وظيفةٍ يجعل المقارنة
 * بين اثنين على وظيفتين مستحيلة.
 */
export const EVALUATION_CRITERIA = [
  'SKILL',
  'EXPERIENCE',
  'COMMUNICATION',
  'FIT',
  'MOTIVATION',
] as const;
export type EvaluationCriterion = (typeof EVALUATION_CRITERIA)[number];

export interface CriterionDef {
  key: EvaluationCriterion;
  label: string;
  hint: string;
}

export const EVALUATION_CRITERION_DEFS: readonly CriterionDef[] = [
  {
    key: 'SKILL',
    label: 'المهارة التقنية',
    hint: 'هل يُتقن ما ستُسنده إليه فعلًا، لا ما يعرف الحديث عنه.',
  },
  {
    key: 'EXPERIENCE',
    label: 'الخبرة ذات الصلة',
    hint: 'سنواتٌ في عملٍ يشبه هذا، لا سنواتٌ في سوق العمل.',
  },
  {
    key: 'COMMUNICATION',
    label: 'التواصل',
    hint: 'يشرح ويسأل ويُنصت. وفي وظائف العملاء هذا نصفُ العمل.',
  },
  {
    key: 'FIT',
    label: 'الانسجام مع الفريق',
    hint: 'طريقةُ عمله مع من سيعمل معهم — لا مدى مشابهته لهم.',
  },
  {
    key: 'MOTIVATION',
    label: 'الدافع',
    hint: 'لماذا هذه الوظيفة بالذات، وهل ما يريده موجودٌ عندنا.',
  },
] as const;

export const EVALUATION_CRITERION_MAP = Object.fromEntries(
  EVALUATION_CRITERION_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<EvaluationCriterion, CriterionDef>>;

/** أدنى وأعلى درجة. سلّمٌ من خمسٍ لأنّ من سبعٍ لا يُميَّز وسطُه. */
export const SCORE_MIN = 1;
export const SCORE_MAX = 5;

export const SCORE_LABELS: Readonly<Record<number, string>> = {
  1: 'ضعيف',
  2: 'دون المطلوب',
  3: 'مقبول',
  4: 'جيّد',
  5: 'ممتاز',
};

/* ==========================================================================
   التوصية
   ========================================================================== */

/**
 * التوصيةُ هي القرار، لا المتوسّط.
 *
 * المتوسّطُ يرتّب المتقاربين ولا يقرّر: مرشّحٌ متوسّطُه أربعةٌ وموصًى
 * برفضه لسببٍ لا يُقاس — كذبٌ في السيرة مثلًا — يجب أن يُرفض. ونظامٌ
 * يقلب الحسابَ إلى قرارٍ يجعل المقيّم يكتب الأرقامَ التي تُنتج ما يريد.
 */
export const RECOMMENDATIONS = [
  'STRONG_YES',
  'YES',
  'MAYBE',
  'NO',
] as const;
export type Recommendation = (typeof RECOMMENDATIONS)[number];

export interface RecommendationDef extends Labelled<Recommendation> {
  description: string;
  /** درجةُ الترجيح عند جمع آراء عدّة مقيّمين. */
  weight: number;
}

export const RECOMMENDATION_DEFS: readonly RecommendationDef[] = [
  {
    key: 'STRONG_YES',
    label: 'أرشّحه بقوّة',
    kind: 'success',
    description: 'أسعى لتعيينه، وأخشى أن يأخذه غيرُنا.',
    weight: 2,
  },
  {
    key: 'YES',
    label: 'أرشّحه',
    kind: 'info',
    description: 'يصلح للوظيفة، ولا تحفّظ جوهريًّا.',
    weight: 1,
  },
  {
    key: 'MAYBE',
    label: 'متردّد',
    kind: 'warning',
    description: 'شيءٌ لم يتّضح — يحتاج مرحلةً أخرى أو رأيًا ثانيًا.',
    weight: 0,
  },
  {
    key: 'NO',
    label: 'لا أرشّحه',
    kind: 'danger',
    description: 'لا يصلح لهذه الوظيفة الآن. والسببُ يُكتب.',
    weight: -2,
  },
] as const;

export const RECOMMENDATION_MAP = Object.fromEntries(
  RECOMMENDATION_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<Recommendation, RecommendationDef>>;

/* ==========================================================================
   الحساب
   ========================================================================== */

export interface EvaluationScores {
  [criterion: string]: number;
}

/**
 * متوسّطُ درجاتِ تقييمٍ واحد.
 *
 * المعاييرُ غيرُ المعطاة تُهمَل ولا تُحسب صفرًا: من قابل مرشّحًا في مكالمةٍ
 * قصيرةٍ لا يحكم على انسجامه مع الفريق، وحسابُ ما لم يُقيَّم صفرًا يعاقبه
 * على سؤالٍ لم يُسأل. ويُردّ `null` إن لم يُعطَ شيء.
 */
export function averageScore(scores: EvaluationScores): number | null {
  const values = EVALUATION_CRITERIA.map((k) => scores[k]).filter(
    (v): v is number => typeof v === 'number' && v >= SCORE_MIN && v <= SCORE_MAX,
  );
  if (values.length === 0) return null;
  const sum = values.reduce((a, b) => a + b, 0);
  return Math.round((sum / values.length) * 10) / 10;
}

export interface EvaluationSummary {
  /** عدد التقييمات المكتوبة. */
  count: number;
  /** متوسّطُ المتوسّطات، أو `null` إن لم يُعطَ رقمٌ واحد. */
  average: number | null;
  /** مجموعُ ترجيحات التوصيات — سالبٌ يعني ميلًا إلى الرفض. */
  lean: number;
  /** هل اختلف المقيّمون اختلافًا يستحقّ الوقوف عنده. */
  split: boolean;
}

/**
 * جمعُ آراءِ عدّةِ مقيّمين.
 *
 * الاختلافُ يُعلَن ولا يُذاب في متوسّط: مرشّحٌ رشّحه واحدٌ بقوّةٍ ورفضه
 * آخرُ ليس «متوسّطًا»، هو حالةٌ تحتاج حديثًا بين الاثنين. وإخفاءُ ذلك
 * خلف رقمٍ واحدٍ هو بالضبط ما يجعل قرارَ التعيين يبدو موضوعيًّا وهو ليس.
 */
export function summarize(
  evaluations: readonly { scores: EvaluationScores; recommendation: Recommendation }[],
): EvaluationSummary {
  if (evaluations.length === 0) {
    return { count: 0, average: null, lean: 0, split: false };
  }

  const averages = evaluations
    .map((e) => averageScore(e.scores))
    .filter((v): v is number => v !== null);

  const lean = evaluations.reduce(
    (s, e) => s + (RECOMMENDATION_MAP[e.recommendation]?.weight ?? 0),
    0,
  );

  const hasYes = evaluations.some(
    (e) => e.recommendation === 'YES' || e.recommendation === 'STRONG_YES',
  );
  const hasNo = evaluations.some((e) => e.recommendation === 'NO');

  return {
    count: evaluations.length,
    average:
      averages.length === 0
        ? null
        : Math.round((averages.reduce((a, b) => a + b, 0) / averages.length) * 10) /
          10,
    lean,
    split: hasYes && hasNo,
  };
}
